import { useState, SyntheticEvent } from 'react'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import HandleSearch from './HandleSearch'
import EmailInput from './EmailInput'
import PreviousPartnersList from './PreviousPartnersList'
import { createInvite, InviteErrorCode } from '../../services/inviteService'
import { useSessionStore } from '../../state'

export interface InviteFormProps {
  diveId: string
  hostUid: string
  diveStatus: string
}

const ERROR_MESSAGES: Record<InviteErrorCode, string> = {
  DUPLICATE: 'This person has already been invited to this dive',
  SELF_INVITE: 'You cannot invite yourself',
  HANDLE_NOT_FOUND: 'User not found',
  DIVE_NOT_OPEN: 'Invites can only be sent for open dives',
  NOT_HOST: 'Only the host can send invites',
  AUTH_REQUIRED: 'Please sign in to send invites'
}

const InviteForm = ({ diveId, hostUid, diveStatus }: InviteFormProps) => {
  const currentUser = useSessionStore(s => s.user)
  const [activeTab, setActiveTab] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)

  // Only render when current user is the host and dive status is "open"
  if (!currentUser || currentUser.uid !== hostUid || diveStatus !== 'open') {
    return null
  }

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    setError(null)
  }

  const handleSubmit = async (inviteeData: {
    handle?: string
    email?: string
    userId?: string
  }) => {
    setIsSubmitting(true)
    setError(null)

    try {
      const result = await createInvite(diveId, inviteeData)

      if (result.success) {
        setShowSuccess(true)
      } else {
        setError(ERROR_MESSAGES[result.error] || 'Failed to send invite')
      }
    } catch (err) {
      console.error('[InviteForm] Failed to send invite:', err)
      setError('Failed to send invite. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleHandleSelect = (data: { handle: string; userId: string }) => {
    handleSubmit({ handle: data.handle, userId: data.userId })
  }

  const handleEmailSubmit = (email: string) => {
    handleSubmit({ email })
  }

  const handlePartnerSelect = (data: { handle: string; userId: string }) => {
    handleSubmit({ handle: data.handle, userId: data.userId })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant='fullWidth'
        sx={{ mb: 2 }}
      >
        <Tab label='Handle' disabled={isSubmitting} />
        <Tab label='Email' disabled={isSubmitting} />
        <Tab label='Previous Partners' disabled={isSubmitting} />
      </Tabs>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          opacity: isSubmitting ? 0.5 : 1,
          pointerEvents: isSubmitting ? 'none' : 'auto'
        }}
      >
        {activeTab === 0 && <HandleSearch onSelect={handleHandleSelect} />}
        {activeTab === 1 && <EmailInput onSubmit={handleEmailSubmit} />}
        {activeTab === 2 && (
          <PreviousPartnersList
            hostUid={hostUid}
            diveId={diveId}
            onSelect={handlePartnerSelect}
          />
        )}
      </Box>

      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity='success' onClose={() => setShowSuccess(false)}>
          Invite sent successfully!
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default InviteForm
